import * as React from 'react';
import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from "react-i18next";
import { LOANS } from "../../locales/namespaces";
import {
	Card,
	CardContent,
	Divider,
	MenuItem,
	Select,
	TextField,
	Typography,
	List,
	ListItem,
	ListItemText,
	Stack,
	Button,
	Grid,
	Box,
	Popover,
	useTheme,
	InputAdornment,
	TableContainer,
	Table,
	TableHead,
	TableRow,
	TableCell,
	TableBody
} from "@mui/material";
import useDocuments from "../../../parameters/hooks/useDocuments";
import { enqueueSnackbar } from "notistack";
import { textTranslate, toCamelCase } from "../../../../helpers/helpers";
import useUserData from "../../../../hooks/user/useUserData";
import moment from "moment";
import { styled } from '@mui/material/styles';
import PaperUpload from "../../../../assets/images/icons/buttonsIcons/paperUpload";
import Search from "../../../../assets/images/icons/search";
import ArrowDown2 from "../../../../assets/images/icons/arrowDown2";
import Polygon3Icon from "../../../../assets/images/icons/polygon3Icon";
import Document from "../../../../assets/images/icons/loan/document";
import { Capacitor } from "@capacitor/core";

const DocumentsList = ({ loansDocuments, handleUpdateDocuments, updateDocuments, status }) => {
	const { t } = useTranslation(LOANS)
	const theme = useTheme();
	const { language } = useUserData()
	const { store, uploading } = useDocuments({ updateDocuments, handleUpdateDocuments });
	const [anchorEl, setAnchorEl] = useState(null);
	const [popoverContent, setPopoverContent] = useState('');
	const [filterValue, setFilterValue] = React.useState('');
	const [selectedStatusId, setSelectedStatusId] = useState('all');

	//Άνοιγμα button για προβολή πλήρους τίτλου περιγραφή εγγράφου
	const handlePopoverOpen = (event, content) => {
		setAnchorEl(event.currentTarget);
		setPopoverContent(content);
	};

	//Κλείσιμο button προβολής πλήρους τίτλου περιγραφή εγγράφου
	const handlePopoverClose = () => {
		setAnchorEl(null);
		setPopoverContent('');
	};

	//Map για τα statuses δανείου
	const statusOptions = status.map((option) => ({
		id: option.id,
		label: textTranslate(language)({
			en: option.DescriptionEn,
			el: option.DescriptionEl,
		}),
	}));

	//Φιλτράρισμα λίστας πληκτρολογώντας όνομα εγγράφου
	const handleFilterChange = (event) => {
		setFilterValue(event.target.value);
	};

	//Φιλτράρισμα λίστας βάση του status που έχει επιλεχθεί
	const handleStatusChange = (event) => {
		setSelectedStatusId(event.target.value);
	};

	//Filter για αναζήτηση από τη λίστα βάση ονόματος περιγραφής και κατάστασης που έχει επιλεχθεί
	const filteredDocuments = useMemo(() =>
		loansDocuments.filter((document) =>
			document.sortDescription.toLowerCase().includes(filterValue.toLowerCase()) &&
			(selectedStatusId === 'all' || document.StatusId === selectedStatusId)
		),
		[loansDocuments, filterValue, selectedStatusId]
	);

	//Button για ανέβασμα εγγράφου με μήνυμα λάθους εάν είναι πάνω από 50ΜΒ ή δεν είναι στη μορφή pdf
	const UploadButton = ({ LoanRelId, LoanId }) => {
		const handleFileUpload = async (event) => {
			const file = event.target.files[0];
			const allowedFileTypes = [
				".doc",
				".docx",
				"application/msword",
				"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
				"application/pdf",
				"image/jpeg",
				"image/jpg",
				"image/png",
				"application/x-zip-compressed",
				"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
				"application/zip",
				"application/x-7z-compressed",
				"application/vnd.ms-word.document.macroEnabled.12",
				"text/plain"
			];

			if (file && file.size > 50 * 1024 * 1024) {
				enqueueSnackbar(t('File size exceeds 50MB.'), {
					variant: 'error'
				}
				)
				return;
			}
			if (!allowedFileTypes.includes(file.type)) {
				enqueueSnackbar(t('Invalid file type.'), {
					variant: 'error'
				});
				return;
			}

			const newRow = {
				file: file,
				LoanRelId: LoanRelId,
				LoanId: LoanId
			};

			store(newRow);
		};

		return (
			//Δημιουργία button ανεβάσματος εγγράφου
			<Stack alignItems="center" spacing={2} 					
				sx={{
				alignItems:{xs:'end', sm:'end',md:'center'}}}
			>
				<Button
					sx={{
						backgroundColor:'transparent !important',
						marginRight: '0 !important',
						border: 'none',
						'&:hover': {
							border: 'none',
						},

					}}
					variant="outlined"
					color={'info'}
					component="label"
					size="small"
					disabled={uploading}
				>
					{uploading ? t("Uploading...") : <PaperUpload />}
					<input
						hidden
						//accept="application/pdf, image/jpeg, image/jpg, image/png, application/zip, application/x-7z-compressed"
						accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.png,.jpg,.jpeg,application/pdf,image/jpeg"
						type="file"
						onChange={handleFileUpload}
					/>
				</Button>
			</Stack>
		);
	};

	const StyledTableRow = styled(TableRow)(({ theme }) => ({
		'&:nth-of-type(odd)': {
			backgroundColor: '#F5F7FC', 
		  },
		  '&:nth-of-type(even)': {
			backgroundColor: '#FFFFFF',
		  },
		  '&:last-child td, &:last-child th': {
			border: 0, 
		  },
		  '&:hover': {
			backgroundColor: '#D9D9D9',
		  }
	  }));

	//Map ανάλογα με την περιγραφή του ονόματος του εγγράφου εάν είναι πάνω από 20 χαρακτήρες ώστε να εμφανιστεί η σύντομη περιγραφή μαζί με button για πληροφορίες για τον πλήρη τίτλο
	const rows = useMemo(
		() => filteredDocuments.map(document => {
			const isDescriptionTooLong = document.Description.length > 3;
			//const descriptionContent = isDescriptionTooLong ? document.Description : '';
			const descriptionText = isDescriptionTooLong
				? textTranslate(language)({
					en: document.sortDescriptionEn,
					el: document.sortDescription
				})
				: textTranslate(language)({
					en: document.sortDescriptionEn,
					el: document.sortDescription
				});

			return (
				//Δημιουργία λίστας εγγράφων με 5 στήλες περιγραφή, όνομα εγγράφου εάν έχει ανέβει, κατάσταση, ημερομηνία που ανέβηκε και το button για το upload
				<StyledTableRow hover key={document.DocTypeId}>

					<TableCell sx={{ p: '0 0 0 24px', border:'none' }} component="th" scope="row" className="table-cell-mobile">
						<Box className="flex-center-left" sx={{ lineHeight: '0' }}>
							{isDescriptionTooLong && (
								<Box sx={{ margin: '20px 12px' }}
									onClick={(event) => handlePopoverOpen(event, textTranslate(language)({
										en: document.DescriptionEn,
										el: document.Description
									}))}
								>
									<Document/>
								</Box>
							)}
							<Typography variant="bodyMedium" sx={{ margin: '20px 12px', color:'#485476', fontSize:'14px' }}>
								{descriptionText}
							</Typography>
						</Box>
					</TableCell>



					<TableCell sx={{ p: '0 0 0 24px', border:'none' }} component="th" scope="row" className="table-cell-mobile">
						<Typography sx={{ color:'#8891AC', fontWeight:'500'}}>
							{!document.StatusEl ? '-' : textTranslate(language)({
								en: document.StatusEn/* && toCamelCase(document.StatusEn)*/,
								el: document.StatusEl/* && toCamelCase(document.StatusEl)*/
							})}
						</Typography>
					</TableCell>

					<TableCell sx={{ p: '0 0 0 24px', border:'none', border:'none' }} component="th" scope="row" className="table-cell-mobile">
						<Typography sx={{ color:'#8891AC', fontWeight:'500'}}>
							{document.OriginalFileName}
						</Typography>
						<Typography  sx={{ color:'#8891AC', fontWeight:'500'}}>
							{!document.DateAdded ? null : moment(document.DateAdded).format("DD-MM-YYYY")}
						</Typography>
					</TableCell>

					<TableCell sx={{ p: '0 0 0 24px', border:'none' }} component="th" scope="row" className="table-cell-mobile">
						<UploadButton
							LoanRelId={document.LoanRelId}
							LoanId={document.LoanId}
						/>
					</TableCell>
				</StyledTableRow>
			)
		}), [filteredDocuments, updateDocuments, handleUpdateDocuments, uploading, loansDocuments])



	return (
		<Box sx={{ border: '0.6px solid #DAE0F0', borderRadius: '12px', overflow: 'hidden' }} className="documents-list-table-cnt">
			{/* Φίλτρα λίστας*/}
			<Grid container columnSpacing={{ xs: 1, sm: 2, md: 4 }} sx={{ padding: '12px' }} className='search-loan-documents'>
				<Grid item xs={6} sm={6} md={6} lg={6}>
					<TextField
						className='inputText documents-search'
						fullWidth
						sx={{ mr: 1, '& .MuiInputBase-input.MuiOutlinedInput-input':{
							paddingLeft:{xs:'0px !important', sm:'12px !important', md:'12px !important' }
						}}}
						id="filter-input"
						placeholder={t('Search...')}
						InputProps={{
							startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
						}}
						variant="outlined"
						value={filterValue}
						onChange={handleFilterChange}
					/>
				</Grid>
				<Grid item xs={6} sm={6} md={6} lg={6}>
					{Capacitor.getPlatform() === 'android' ?
						<Select
							className='inputText documents-search'
							native
							value={selectedStatusId}
							onChange={handleStatusChange}
							sx={{ minWidth: 150 }}
							fullWidth
							IconComponent={ArrowDown2}
						>
							<option className="p-sm" value="all">{t('Select all')}</option>
							{statusOptions.map((option) => (
								<option className="p-sm" key={option.id} value={option.id}>
									{option.label}
								</option>
							))}
						</Select> :
						<Select
							className='inputText documents-search'
							value={selectedStatusId}
							onChange={handleStatusChange}
							sx={{ minWidth: 150 }}
							fullWidth
							IconComponent={ArrowDown2}
						>
							<MenuItem className="p-sm" value="all">{t('Select all')}</MenuItem>
							{statusOptions.map((option) => (
								<MenuItem className="p-sm" key={option.id} value={option.id}>
									{option.label}
								</MenuItem>
							))}
						</Select>}
				</Grid>
			</Grid>
			{/*Card σε περίπτωση που το υπάρχον status δε χρειάζεται έγγραφα να γίνουν upload*/}
			<Box sx={{ minHeight: 400, maxHeight: 600, overflow: 'auto' }}>
				<Divider sx={{ mb: 1 }} light />
				{rows.length <= 0 || filteredDocuments.length === 0 &&
					<Card sx={{ mb: 1, mt: 1 }}>
						<CardContent>
							<Typography sx={{
								mt: 1,
								mb: 1,
								ml: 1,
								mr: 1
							}}>{t('There are currently no documents to upload.')}
							</Typography>
						</CardContent>
					</Card>
				}
				<TableContainer>
					<Table sx={{ minWidth: 350 }} aria-label="repayments table">
						<TableHead sx={{border:'none'}} className='thead-mobile'>
							<TableRow>
								<TableCell
									sx={{
										width: '30%',
										borderBottom: 'none', 
										'&:not(:last-of-type)::after': { content: 'none' },
									}}
								>
									{t("Required Documents")}
								</TableCell>
								<TableCell
									sx={{
										width: '20%',
										borderBottom: 'none', 
										'&:not(:last-of-type)::after': { content: 'none' },
									}}
									align="center"
								>
									{t("Control Status")}
								</TableCell>
								<TableCell
									sx={{
										width: '40%',
										borderBottom: 'none',
										'&:not(:last-of-type)::after': { content: 'none' }, 
									}}
									align="center"
								>
									{t("Sent files")}
								</TableCell>
								<TableCell
									sx={{
										width: '10%',
										borderBottom: 'none',
										'&:not(:last-of-type)::after': { content: 'none' },
									}}
									align="right"
								>
								</TableCell>
							</TableRow>
						</TableHead>

						<TableBody>{rows}</TableBody>
					</Table>
				</TableContainer>



			</Box>
			{/*Popover για περισσότερες λεπτομέρειες τίτλου περιγραφής εγγράφου ήταν πολύ μεγάλος*/}
			<Popover
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={handlePopoverClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'center',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'left',
				}}
				sx={{
					'.MuiPaper-root': {
						background: 'transparent',
						overflowY: 'hidden',
					}
				}}
			>
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'flex-start',
						gap: '-4px',
						flexShrink: 0,
					}}
				>
					<Polygon3Icon />
					<Box
						sx={{
							background: theme.palette.primary[100],
							display: 'flex',
							padding: '8px 17px 21px 17px',
							flexDirection: 'column',
							alignItems: 'flex-end',
							gap: '20px',
							maxWidth: '1000px',
							borderRadius: '0px 12px 12px 12px',
							m: -0.3,
							minWidth: '100px'
						}}
					>
						<Button
							onClick={handlePopoverClose}
							color={'primary'}
							sx={{
								height: '13px',
								minWidth: '9px',
								maxWidth: '9px',
								color: '#FFF',
								textAlign: 'right',
								fontSize: '14px',
								fontStyle: 'normal',
								fontWeight: 700,
								lineHeight: '22px',
								// m:1,
								'&:hover': {
									color: '#fff'
								}
							}}
						>x</Button>
						<Typography
							variant={'h5'}
							sx={{
								color: '#fff'
							}}
						>{popoverContent}</Typography>
					</Box>
				</div>
			</Popover>
		</Box>
	)
}

export default DocumentsList